import { useEffect, useState } from "react";
import {
  FileText, Sparkles, Loader2, AlertTriangle, CheckCircle2, Clock, Eye, Download, Send,
} from "lucide-react";
import { usePrdStore } from "../../store/usePrdStore";
import PrdWorkspaceModal from "./PrdWorkspaceModal";

// ── The PRD block in Step 3 — Documents & Assets ─────────────────────────────
//
// It sits beside the Job Description and Project Documents blocks and behaves
// like them: optional, and it never blocks finishing the project.
//
// The one thing it needs that the other two do not is a project to attach to.
// Generation reads milestones, questionnaire answers and uploaded files, none
// of which exist until the wizard has saved something — so rather than putting
// a disabled button behind an explanation, the button saves the draft and then
// generates. The person asked for a PRD; saving first is our problem.

interface Props {
  projectId: string | null;
  projectName: string;
  projectCategory: string;
  // Saves the wizard as a draft and returns the project id. Supplied by the
  // form, because only the form knows what is currently entered.
  ensureProject: () => Promise<string | null>;
  canSeeCommercial?: boolean;
}

const STATUS_TONE: Record<string, string> = {
  Draft: "bg-slate-100 text-slate-700 border-slate-200",
  "In Review": "bg-amber-50 text-amber-800 border-amber-200",
  Approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Changes Required": "bg-rose-50 text-rose-800 border-rose-200",
  Outdated: "bg-orange-50 text-orange-800 border-orange-200",
};

const PrdSection = ({ projectId, projectName, projectCategory, ensureProject, canSeeCommercial = false }: Props) => {
  const { prd, permissions, template, categories, busy, loading, fetch, generate, download, reset } = usePrdStore();

  const [open, setOpen] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [pickCategory, setPickCategory] = useState("");
  const [includeCommercial, setIncludeCommercial] = useState(false);

  useEffect(() => {
    if (projectId) fetch(projectId);
    else reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const working = Boolean(busy) || preparing || loading;

  const run = async () => {
    setPreparing(true);
    try {
      // Save first if we have to — the wizard's current state is the source
      // material, and generating from a half-saved project is worse than
      // waiting a second.
      const id = projectId || (await ensureProject());
      if (!id) return;
      if (!projectId) await fetch(id);

      const res = await generate(id, {
        ...(pickCategory ? { confirmedCategory: pickCategory } : {}),
        ...(includeCommercial ? { includeCommercial: true } : {}),
      });
      // 422 — the server needs a category decision. The picker below shows it.
      if (res && !res.ok) {
        usePrdStore.setState({ template: res });
        return;
      }
      setOpen(true);
    } finally {
      setPreparing(false);
    }
  };

  const blocked = template && !template.ok ? template : null;
  const flagged =
    (prd?.aiReview?.missing?.length || 0) +
    (prd?.aiReview?.conflicts?.length || 0) +
    (prd?.aiReview?.unreadableSources?.length || 0);

  return (
    <>
      <div className="rounded-xl border border-outline-variant p-3.5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-on-surface flex items-center gap-1.5">
              <FileText size={14} className="text-primary" />
              Project Requirements Document (PRD)
            </p>
            <span className="text-[11px] text-on-surface-variant">
              {prd
                ? `${prd.templateName}${prd.approvedByName ? ` · approved by ${prd.approvedByName}` : ""}`
                : "Generated from this project's information, milestones, documents and questionnaire answers. Optional."}
            </span>
          </div>

          {prd && (
            <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide shrink-0 ${STATUS_TONE[prd.status] || ""}`}>
              {prd.status}
            </span>
          )}
        </div>

        {/* No PRD yet */}
        {!prd && (
          <>
            {blocked && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 space-y-2">
                <p className="text-[11.5px] text-amber-900 flex items-start gap-1.5">
                  <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                  {blocked.message}
                </p>
                {!!blocked.candidates?.length && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={pickCategory}
                      onChange={(e) => setPickCategory(e.target.value)}
                      className="px-2 py-1.5 bg-surface-container-lowest border border-outline-variant rounded-lg text-[11.5px] text-on-surface outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Confirm the primary category…</option>
                      {(blocked.candidates || categories).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <span className="text-[10.5px] text-amber-800">
                      A template from another category is never substituted automatically.
                    </span>
                  </div>
                )}
              </div>
            )}

            {canSeeCommercial && (
              <label className="flex items-center gap-2 text-[11.5px] text-on-surface-variant">
                <input
                  type="checkbox"
                  checked={includeCommercial}
                  onChange={(e) => setIncludeCommercial(e.target.checked)}
                  className="rounded border-outline-variant"
                />
                Include commercial terms (value, rate, payment schedule)
              </label>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={run}
                disabled={working || Boolean(blocked?.candidates?.length && !pickCategory) || blocked?.code === "NO_TEMPLATE"}
                className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-bold hover:brightness-110 flex items-center gap-1.5 disabled:opacity-50"
              >
                {working ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                {preparing && !projectId ? "Saving draft…" : busy === "generate" ? "Reading your sources…" : "Generate PRD"}
              </button>
              <span className="text-[11px] text-on-surface-variant">
                {projectId
                  ? `Uses the ${projectCategory || "project"} template.`
                  : "The project is saved as a draft first so its files and milestones can be read."}
              </span>
            </div>
          </>
        )}

        {/* A PRD exists */}
        {prd && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { label: "Requirements", value: prd.requirements?.length || 0, icon: <CheckCircle2 size={12} className="text-emerald-600" /> },
                { label: "To review", value: flagged, icon: <AlertTriangle size={12} className={flagged ? "text-amber-600" : "text-on-surface-variant"} /> },
                { label: "Your edits", value: prd.editedSections?.length || 0, icon: <FileText size={12} className="text-on-surface-variant" /> },
                {
                  label: "ERP",
                  value: prd.erp?.status === "Synced" ? "Linked" : prd.status === "Approved" ? "Pending" : "—",
                  icon: <Send size={12} className={prd.erp?.status === "Synced" ? "text-emerald-600" : "text-on-surface-variant"} />,
                },
              ].map((s) => (
                <div key={s.label} className="rounded-lg bg-surface-container-low px-2.5 py-1.5">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-on-surface-variant flex items-center gap-1">
                    {s.icon} {s.label}
                  </span>
                  <span className="text-[13px] font-bold text-on-surface">{s.value}</span>
                </div>
              ))}
            </div>

            {prd.status === "Outdated" && (
              <p className="text-[11px] text-orange-800 bg-orange-50 border border-orange-200 rounded-lg px-2.5 py-1.5 flex items-start gap-1.5">
                <Clock size={12} className="shrink-0 mt-0.5" />
                The project changed after approval ({prd.outdatedReasons.join("; ")}). The approved document is untouched.
              </p>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-bold hover:brightness-110 flex items-center gap-1.5"
              >
                <Eye size={13} /> {permissions?.edit ? "Review & edit" : "View"}
              </button>
              {permissions?.download && prd.artifact && (
                <button
                  type="button"
                  onClick={() => download(prd._id, prd.artifact!.fileName)}
                  disabled={working}
                  className="px-3 py-1.5 bg-surface-container-low border border-outline-variant text-on-surface rounded-lg text-xs font-semibold hover:bg-surface-container flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Download size={13} /> Download
                </button>
              )}
              {prd.status === "Draft" && (
                <span className="text-[11px] text-on-surface-variant">
                  {permissions?.approve
                    ? "Not approved yet — review it and approve when it reads right."
                    : "Not yet submitted for approval."}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {open && <PrdWorkspaceModal projectName={projectName} onClose={() => setOpen(false)} />}
    </>
  );
};

export default PrdSection;
